import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getPublicBlogs, subscribeNewsletter } from '../../utils/api';
import Icon from '../../components/AppIcon';
import NavigationHeader from '../../components/ui/NavigationHeader';
import Footer from '../../components/ui/Footer';

const ARTICLES_PER_PAGE = 6;

const BlogsList = () => {
    const navigate = useNavigate();
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [visibleCount, setVisibleCount] = useState(ARTICLES_PER_PAGE);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        const fetchBlogs = async () => {
            try {
                const data = await getPublicBlogs();
                setBlogs(data || []);
            } catch (error) {
                console.error("Failed to fetch blogs:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchBlogs();
    }, []);

    // Filter blogs by search
    const filteredBlogs = blogs.filter(b => {
        if (!searchQuery.trim()) return true;
        const q = searchQuery.toLowerCase();
        return (
            (b.title || '').toLowerCase().includes(q) ||
            (b.summary || '').toLowerCase().includes(q) ||
            (b.author_full_name || '').toLowerCase().includes(q) ||
            (b.tags || []).some(t => t.toLowerCase().includes(q))
        );
    });

    const featured = filteredBlogs[0];
    const latestBlogs = filteredBlogs.slice(1, visibleCount + 1);
    const mostRead = blogs.slice(0, 5);
    const topAuthors = [...new Map(blogs.filter(b => b.author_full_name).map(b => [b.author_full_name, b])).values()].slice(0, 3);

    const [newsletterEmail, setNewsletterEmail] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [subscribeStatus, setSubscribeStatus] = useState(null); // 'success', 'error', null

    const handleSubscribe = async (e) => {
        e.preventDefault();
        if (!newsletterEmail) return;
        setSubmitting(true);
        try {
            await subscribeNewsletter(newsletterEmail);
            setSubscribeStatus('success');
            setNewsletterEmail('');
            setTimeout(() => setSubscribeStatus(null), 5000);
        } catch (error) {
            console.error("Newsletter subscription failed:", error);
            setSubscribeStatus('error');
            setTimeout(() => setSubscribeStatus(null), 5000);
        } finally {
            setSubmitting(false);
        }
    };

    // Skeleton loader
    if (loading) {
        return (
            <div className="min-h-screen bg-white font-sans relative overflow-hidden" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {/* Premium Header Background */}
                <div className="absolute top-0 left-0 right-0 h-[450px] bg-gradient-to-b from-emerald-50 via-emerald-50/20 to-white pointer-events-none z-0" />
                <NavigationHeader />
                <div className="max-w-7xl mx-auto px-6 pt-24 space-y-8 relative z-10">
                    <div className="h-[400px] bg-slate-50 rounded-lg animate-pulse" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {[1, 2, 3, 4].map(i => (
                            <div key={i} className="h-[320px] bg-slate-50 rounded-lg animate-pulse" />
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white font-sans relative overflow-hidden" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            {/* Premium Header Background */}
            <div className="absolute top-0 left-0 right-0 h-[450px] bg-gradient-to-b from-emerald-50 via-emerald-50/20 to-white pointer-events-none z-0" />
            <NavigationHeader />

            <div className="pt-16 pb-2 px-6 max-w-7xl mx-auto w-full flex flex-col md:flex-row md:items-center justify-between gap-4 relative z-10">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Link to="/dashboard" className="hover:text-slate-900 transition-colors">
                        Dashboard
                    </Link>
                    <Icon name="ChevronRight" size={16} />
                    <span className="text-slate-900 font-medium">Blog</span>
                </div>

                <div className="flex items-center gap-3">
                    <div className="relative hidden sm:block">
                        <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search articles..."
                            value={searchQuery}
                            onChange={e => { setSearchQuery(e.target.value); setVisibleCount(ARTICLES_PER_PAGE); }}
                            className="pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-emerald-400 transition-all w-60"
                        />
                    </div>
                    <button onClick={() => navigate('/blog-submit')} className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-boldtracking-wider px-5 py-2.5 rounded-lg transition-all shadow-sm active:scale-95">
                        <Icon name="PenLine" size={14} /> Write Post
                    </button>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-4 relative z-10">

                {/* ── FEATURED HERO ── */}
                {featured && (
                    <section className="pb-10 pt-4 border-b border-slate-100">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                            <Link to={`/blogs/${featured.slug}`} className="block aspect-[4/3] rounded-2xl overflow-hidden bg-slate-50 group border border-slate-100">
                                {featured.cover_image_url ? (
                                    <img src={featured.cover_image_url} alt={featured.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-300">
                                        <Icon name="Image" size={48} />
                                    </div>
                                )}
                            </Link>
                            <div className="flex flex-col justify-center">
                                <div className="flex items-center gap-3 mb-4">
                                    <span className="text-xs font-boldtracking-[0.15em] text-emerald-600">Featured</span>
                                    <span className="w-8 h-[1px] bg-gray-300" />
                                    <span className="text-xs font-mediumtracking-wider text-gray-400">
                                        {featured.read_time_minutes || 5} min read
                                    </span>
                                </div>
                                <Link to={`/blogs/${featured.slug}`}>
                                    <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-[1.15] tracking-tight mb-4 hover:text-emerald-700 transition-colors">
                                        {featured.title}
                                    </h1>
                                </Link>
                                <p className="text-gray-500 leading-relaxed mb-6 line-clamp-3">
                                    {featured.summary || "Click to read the full article."}
                                </p>
                                <div className="flex items-center gap-4 text-sm text-gray-400 mb-6">
                                    {featured.author_full_name && (
                                        <span className="flex items-center gap-2">
                                            <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs font-bold">
                                                {featured.author_full_name.charAt(0)}
                                            </span>
                                            <span className="font-medium text-gray-600">by {featured.author_full_name}</span>
                                        </span>
                                    )}
                                    <span className="flex items-center gap-1">
                                        <Icon name="Eye" size={14} className="text-emerald-500" />
                                        {featured.views_count || 0} Views
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <Icon name="Heart" size={14} className="text-rose-400" />
                                        {featured.likes_count || 0} Likes
                                    </span>
                                </div>
                                <Link
                                    to={`/blogs/${featured.slug}`}
                                    className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-boldtracking-wider px-6 py-3 rounded transition-colors w-fit"
                                >
                                    Read Article
                                </Link>
                            </div>
                        </div>
                    </section>
                )}

                {/* ── MAIN CONTENT ── */}
                <section className="py-10">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                        {/* Left: Articles Grid */}
                        <div className="lg:col-span-2">
                            <h2 className="text-xs font-boldtracking-[0.2em] text-gray-400 mb-8">Latest Insights</h2>

                            {blogs.length <= 1 ? (
                                <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-slate-200">
                                    <Icon name="BookOpen" className="text-slate-200 mx-auto mb-3" size={32} />
                                    <p className="text-slate-400 font-medium tracking-tight">No more posts yet. Check back soon!</p>
                                </div>
                            ) : (
                                <>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        {latestBlogs.map((blog) => (
                                            <ArticleCard key={blog.id} blog={blog} />
                                        ))}
                                    </div>
                                    {visibleCount + 1 < blogs.length && (
                                        <div className="text-center mt-12">
                                            <button
                                                onClick={() => setVisibleCount(c => c + ARTICLES_PER_PAGE)}
                                                className="text-xs font-boldtracking-widest text-slate-600 border border-slate-200 px-8 py-3.5 rounded-full hover:border-emerald-500 hover:text-emerald-600 transition-all active:scale-95"
                                            >
                                                Load More Articles
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Right: Sidebar */}
                        <aside className="space-y-8">
                            {/* Most Read */}
                            <div>
                                <h3 className="flex items-center gap-2 text-xs font-boldtracking-[0.2em] text-gray-400 mb-5">
                                    <Icon name="TrendingUp" size={14} /> Most Read This Week
                                </h3>
                                <div className="space-y-4">
                                    {mostRead.map((blog, i) => (
                                        <Link key={blog.id} to={`/blogs/${blog.slug}`} className="flex items-start gap-3 group">
                                            <span className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center text-xs font-bold text-gray-400 group-hover:border-emerald-400 group-hover:text-emerald-600 transition-colors flex-shrink-0 mt-0.5">
                                                {i + 1}
                                            </span>
                                            <span className="text-sm font-semibold text-gray-800 leading-snug group-hover:text-emerald-600 transition-colors line-clamp-2">
                                                {blog.title}
                                            </span>
                                        </Link>
                                    ))}
                                </div>
                            </div>

                            {/* Top Authors */}
                            {topAuthors.length > 0 && (
                                <div>
                                    <h3 className="text-xs font-boldtracking-[0.2em] text-gray-400 mb-5">Top Authors</h3>
                                    <div className="space-y-3">
                                        {topAuthors.map((blog, i) => (
                                            <div key={i} className="flex items-center gap-3">
                                                <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white ${['bg-emerald-500', 'bg-violet-500', 'bg-amber-500'][i % 3]}`}>
                                                    {blog.author_full_name.charAt(0)}
                                                </span>
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-800">{blog.author_full_name}</p>
                                                    <p className="text-xs text-gray-400">prepxai Contributor</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Newsletter Improvement */}
                            <div className="bg-slate-900 rounded-3xl border border-slate-800 p-8 text-white shadow-xl relative overflow-hidden group">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-full -mr-16 -mt-16 blur-2xl group-hover:bg-indigo-500/20 transition-all duration-700" />
                                <div className="absolute bottom-0 left-0 w-24 h-24 bg-emerald-500/5 rounded-full -ml-12 -mb-12 blur-2xl group-hover:bg-emerald-500/10 transition-all duration-700" />

                                <div className="relative z-10">
                                    <div className="flex items-center gap-2 text-emerald-400 mb-4">
                                        <Icon name="Mail" size={16} className="animate-bounce" />
                                        <span className="text-[11px] font-bold tracking-[0.2em] uppercase">Newsletter</span>
                                    </div>
                                    <h4 className="text-xl font-bold mb-2 tracking-tight">Stay ahead of the curve.</h4>
                                    <p className="text-sm text-slate-400 mb-6 leading-relaxed">Get the latest engineering insights delivered to your inbox.</p>

                                    <form onSubmit={handleSubscribe} className="space-y-3">
                                        <div className="relative">
                                            <Icon name="AtSign" size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                                            <input
                                                type="email"
                                                required
                                                value={newsletterEmail}
                                                onChange={(e) => setNewsletterEmail(e.target.value)}
                                                placeholder="Enter your email"
                                                className="w-full bg-slate-800/50 border border-slate-700 rounded-xl pl-11 pr-4 py-3.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all"
                                            />
                                        </div>
                                        <button 
                                            type="submit" 
                                            disabled={submitting}
                                            className="w-full bg-emerald-500 hover:bg-emerald-400 disabled:bg-slate-700 text-white text-[12px] font-blacktracking-widest py-3.5 rounded-xl transition-all shadow-lg shadow-emerald-500/20 active:scale-[0.98]"
                                        >
                                            {submitting ? 'Joining...' : 'Join 20K+ Engineers'}
                                        </button>
                                        {subscribeStatus === 'success' && (
                                            <p className="text-emerald-400 text-[10px] text-center mt-2 font-bold">Successfully subscribed!</p>
                                        )}
                                        {subscribeStatus === 'error' && (
                                            <p className="text-rose-400 text-[10px] text-center mt-2 font-bold">Failed to subscribe. Try again.</p>
                                        )}
                                    </form>
                                    <p className="text-[10px] text-slate-500 text-center mt-4">No spam. Unsubscribe at any time.</p>
                                </div>
                            </div>
                        </aside>
                    </div>
                </section>
            </div>
        </div>
    );
};

/* ── ARTICLE CARD ── */
const ArticleCard = ({ blog }) => {
    const categoryColors = ['text-emerald-600', 'text-indigo-600', 'text-rose-600', 'text-amber-600', 'text-sky-600'];
    const categoryColor = categoryColors[blog.id % categoryColors.length] || 'text-emerald-600';
    const category = blog.tags?.[0] || (blog.read_time_minutes > 7 ? 'Deep Dive' : 'Engineering');

    return (
        <Link to={`/blogs/${blog.slug}`} className="group block">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-slate-50 mb-5 border border-slate-100">
                {blog.cover_image_url ? (
                    <img src={blog.cover_image_url} alt={blog.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-300">
                        <Icon name="Image" size={32} />
                    </div>
                )}
            </div>
            <span className={`text-[10px] font-boldtracking-[0.15em] ${categoryColor} mb-2 block`}>
                #{category}
            </span>
            <h3 className="text-lg font-bold text-gray-900 leading-snug mb-2 group-hover:text-emerald-600 transition-colors line-clamp-2">
                {blog.title}
            </h3>
            <p className="text-sm text-gray-500 line-clamp-2 leading-relaxed mb-4">
                {blog.summary || "Click to read the full article."}
            </p>
            <div className="flex items-center justify-between text-xs text-gray-400">
                <div className="flex items-center gap-2">
                    {blog.author_full_name && (
                        <>
                            <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-[10px] font-bold">
                                {blog.author_full_name.charAt(0)}
                            </span>
                            <span className="font-medium text-gray-600">{blog.author_full_name}</span>
                        </>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1">
                        <Icon name="Eye" size={12} className="text-emerald-500" />
                        {blog.views_count ? (blog.views_count > 999 ? `${(blog.views_count / 1000).toFixed(1)}k` : blog.views_count) : 0}
                    </span>
                    <span className="flex items-center gap-1">
                        <Icon name="Heart" size={12} className="text-rose-400" />
                        {blog.likes_count || 0}
                    </span>
                </div>
            </div>
        </Link>
    );
};

export default BlogsList;
